import React from 'react';
import {Document, Page, Text, View} from '@react-pdf/renderer';
import _ from 'lodash';
import {styles} from './InvoiceTemplate.styles';
import InvoiceFeeItem from '../InvoiceFeeItem';
import {DRIVER, SUITABLE_PAYMENT_TYPES} from '../../constants/dataConfig';
import {displayPriceFormat, isObjectNotEmpty, isStringNotNull} from '../../utils/tools';
import {getDateFormat} from '../../utils/suitableDate';
import {DriverServicePrice, useDriverInvoiceTemplate} from './useDriverInvoiceTemplate';
import {Legal} from '../../models/Legal';
import {Address} from '../../models/Address';
import {BillingDeliveryObject, BillingTransaction} from "../../models/Billing";
import {roundNumber} from "../../utils/numberUtils";


export type DriverInvoiceTemplateProps = {
    legal: Legal;
    year?: number | string;
    billing: BillingTransaction;
    dateInfo?: string;
    creditorLegal?: Legal;
    shopAddress?: Address;
    shopId?: string;
    isNetworkChecked?: boolean;
};

const DriverInvoiceTemplate: React.FC<DriverInvoiceTemplateProps> = (props) => {
    const { driverItems, emptyInvoice, invoiceKey } = useDriverInvoiceTemplate(props);

    if (emptyInvoice) {
        return null;
    }

    const renderInvoiceHeader = () => {
        const {
            legal,
            billing,
            dateInfo
        } = props;

        const {
            address = {} as Address,
            companyName,
            email,
            fullRegistrationNumber,
            registreCommerceNumero,
            vatNumber,
            numeroTVA
        } = (props.creditorLegal || {}) as any;

        const {
            address: debitorAddress = {} as any,
            companyName: debitorCompanyName = '',
            registreCommerceNumero: debitorRegistreCommerceNumero,
            numeroTVA: debitorNumeroTVA
        } = legal || {};

        return (
            <>
                <View style={styles.companies}>
                    <View>
                        <Text style={styles.nameUs}>{companyName || ''}</Text>

                        {isObjectNotEmpty(address) && (
                            <>
                                <Text style={styles.presdishop}>
                                    {address.streetNumber} {address.street},
                                </Text>
                                <Text style={styles.presdishop}>
                                    {address.postalCode}, {address.city},
                                </Text>
                                <Text style={styles.presdishop}>{address.country}</Text>
                            </>
                        )}

                        <Text style={styles.presdishop}>{registreCommerceNumero || ''}</Text>
                        <Text style={styles.presdishop}>{numeroTVA || vatNumber || ''}</Text>
                        <Text style={styles.presdishop}>{email || ''}</Text>

                        {isStringNotNull(fullRegistrationNumber) && (
                            <Text style={styles.presdishop}>RCS - {fullRegistrationNumber}</Text>
                        )}
                    </View>

                    <View style={styles.rightHeader}>
                        <View style={styles.rightLegal}>
                            <Text style={[styles.presdishop, styles.underlinedText, styles.textStyle]}>
                                Facturé à:
                            </Text>

                            <Text style={styles.nameBuis}>{debitorCompanyName}</Text>

                            {isObjectNotEmpty(debitorAddress) && (
                                <>
                                    <Text style={styles.presdishop}>
                                        {`${debitorAddress.streetNumber || ''} ${debitorAddress.street || ''}`},
                                    </Text>
                                    <Text style={styles.presdishop}>
                                        {debitorAddress.postalCode}, {debitorAddress.city},
                                    </Text>
                                    <Text style={styles.presdishop}>{debitorAddress.country}</Text>
                                    <Text style={styles.presdishop}>{debitorRegistreCommerceNumero}</Text>
                                    <Text style={styles.presdishop}>{debitorNumeroTVA}</Text>
                                </>
                            )}
                        </View>
                    </View>
                </View>

                <View style={[styles.ref, styles.alignementStyle]}>
                    <Text style={styles.text}>
                        <Text style={styles.underlinedText}>Référence de facture:</Text>{' '}
                        {invoiceKey}
                    </Text>

                    <Text style={styles.text}>
                        <Text style={styles.underlinedText}>Date d'émission:</Text>{' '}
                        {getDateFormat(dateInfo as any)}
                    </Text>

                    <Text style={styles.text}>
                        <Text style={styles.underlinedText}>Type de paiement:</Text>{' '}
                        {SUITABLE_PAYMENT_TYPES[billing?.paymentType as keyof typeof SUITABLE_PAYMENT_TYPES] || ''}
                    </Text>

                    <Text style={styles.text}>
                        <Text style={styles.underlinedText}>Date de téléchargement:</Text>{' '}
                        {getDateFormat()}
                    </Text>
                </View>
            </>
        );
    };

    const renderDeliveryDetails = () => {
        const { legal, billing, shopAddress = {} } = props;
        const { address = {} } = legal || {};
        const { orderNumber, dateOrder, delivery = {} as BillingDeliveryObject, distance = null } = billing;
        const { address: deliveryAddress = {}, date } = (delivery || {}) as any;
        const { streetNumber, street, postalCode, city } = deliveryAddress || {};
        const pickupAddress = (isObjectNotEmpty(shopAddress) ? shopAddress : address) as any;

        return (
            <View style={styles.contract}>
                <View style={[styles.border, styles.titleContract]}>
                    <Text style={styles.titleTextContract}>Détails de la livraison</Text>
                </View>

                <View style={styles.firstTabValue}>
                    <View style={styles.leftPart}>
                        <View style={styles.rowDetail}>
                            <Text style={styles.titleTab}>Retrait:</Text>
                            <Text style={styles.deliveryLabel}>
                                {` ${getDateFormat(date, 'LT')} | ${pickupAddress.streetNumber || ''} ${
                                    pickupAddress.street || ''
                                }, ${pickupAddress.postalCode || ''}, ${pickupAddress.city || ''}, ${
                                    pickupAddress.country || ''
                                }`}
                            </Text>
                        </View>

                        <View style={styles.rowDetail}>
                            <Text style={styles.titleTab}>Livraison:</Text>
                            <Text style={styles.deliveryLabel}>
                                {` ${getDateFormat(dateOrder as any, 'LT')} | ${
                                    isObjectNotEmpty(deliveryAddress)
                                        ? `${streetNumber} ${street}, ${postalCode ? `${postalCode}, ` : ''}${city}`
                                        : '--'
                                }`}
                            </Text>
                        </View>
                    </View>

                    <View style={styles.rightPart}>
                        <View style={styles.rowDetail}>
                            <Text style={styles.titleTab}>N° livraison:</Text>
                            <Text style={styles.deliveryLabel}>{orderNumber}</Text>
                        </View>

                        <View style={styles.rowDetail}>
                            <Text style={styles.titleTab}>Date de livraison:</Text>
                            <Text style={styles.deliveryLabel}>{getDateFormat(dateOrder as any)}</Text>
                        </View>

                        <View style={styles.rowDetail}>
                            <Text style={styles.titleTab}>Distance parcourue:</Text>
                            <Text style={styles.deliveryLabel}>{delivery?.distance ?? distance ?? 0} km</Text>
                        </View>
                    </View>
                </View>
            </View>
        );
    };

    const renderInvoiceObject = () => {
        return (
            <Text style={[styles.text, styles.ref]}>
                <Text style={styles.underlinedText}>Objet:</Text>{' '}
                Frais pour le service de livraison
            </Text>
        );
    };

    const subjectToTVA = driverItems.some((item) => Boolean(item.subjectToVAT));
    const totalSumFees = roundNumber(_.sumBy(driverItems, 'sumFee'));
    const totalHt = subjectToTVA
        ? roundNumber(
            _.sumBy(driverItems, (item) => {
                return roundNumber(item.sumFee / 1.2);
            })
        )
        : totalSumFees;

    const vatAmount = subjectToTVA ? roundNumber(totalSumFees - totalHt) : 0;
    const isZeroVAT = !subjectToTVA;

    const renderDriverDuty = () => {
        return (
            <View style={[styles.obligationView, styles.topBorder]}>
                {!subjectToTVA && (
                    <Text style={styles.tvaText}> [1] : TVA non applicable, art.293B du CGI.</Text>
                )}

                <Text>
                    En tant que coursier professionnel indépendant, vous êtes tenu de respecter des
                    obligations fiscales et sociales. En effet, les revenus que vous tirez des livraisons de
                    marchandises réalisées par l'intermédiaire de la plateforme Dishop Fleet sont imposables
                    et soumis à cotisations sociales. Vous devez donc déclarer vos revenus à l'administration
                    fiscale ainsi qu'à l'URSSAF afin, ensuite, de payer l'impôt et les cotisations sociales.
                    Vous trouverez, ci-dessous, des liens vers les sites des administrations permettant de
                    vous conformer à vos obligations :
                </Text>

                <View style={styles.link}>
                    <Text>• Sur les obligations fiscales : https://www.impots.gouv.fr/portail/node/10841</Text>
                    <Text>
                        • Sur les obligations sociales :
                        http://www.securite-sociale.fr/Vos-droits-et-demarches-dans-le-cadre-des-activites-economiques-entre-particuliers-Article-87
                    </Text>
                    <Text>
                        • Pour un résumé explicatif de ces obligations :
                        proxy-pubminefi.diffusion.finances.gouv.fr/pub/document/18/22081.pdf
                    </Text>
                </View>
            </View>
        );
    };

    const renderFooter = () => {
        return (
            <Text style={[styles.bottomPage, styles.topBorder]}>
                Des questions ? Contactez Dishop à l'adresse contact@dishop.co ou contactez directement
                votre référent commercial
            </Text>
        );
    };

    return (
        <Document title={`Facture dishop ${invoiceKey}`}>
            <Page style={styles.body}>
                <Text style={styles.title}>Facture</Text>

                {renderInvoiceHeader()}

                <View style={styles.positionTab}>
                    {renderInvoiceObject()}
                    {renderDeliveryDetails()}

                    <View style={styles.contract} wrap={false}>
                        <View style={styles.firstTabValue}>
                            <View style={[styles.border, styles.row, styles.leftPart]}>
                                <Text style={styles.titleTab}>Détails de la facture</Text>
                                <Text style={styles.titleTab}>Qté</Text>
                            </View>

                            <View style={[styles.border, styles.row, styles.rightPart]}>
                                <Text style={styles.titleTab}>Prix unitaire</Text>
                                <Text style={styles.titleTab}>Montant</Text>
                            </View>
                        </View>

                        {driverItems.map((servicePrice: DriverServicePrice, i: number) => {
                            const subtotalHT = servicePrice.subjectToVAT
                                ? roundNumber(servicePrice.sumFee / 1.2)
                                : servicePrice.sumFee;

                            return (
                                <InvoiceFeeItem
                                    key={i.toString()}
                                    serviceBillingItem={driverItems}
                                    servicePrice={servicePrice}
                                    isDriverInvoice
                                    serviceBillingItemKey={DRIVER}
                                    isDishopCreditor={false}
                                    subtotalHT={subtotalHT}
                                />
                            );
                        })}

                        <View style={styles.positionRow}>
                            <View style={styles.leftPart} />
                            <View style={styles.rightPart}>
                                <View style={styles.tableau}>
                                    <Text style={styles.labelTab}>Total HT</Text>
                                    <Text style={styles.labelTab}>{displayPriceFormat(totalHt)}</Text>
                                </View>

                                {isZeroVAT ? (
                                    <View style={[styles.tableau, styles.border]}>
                                        <Text style={styles.labelTab}>TVA (0 %) [1]</Text>
                                        <Text style={styles.labelTab}>{displayPriceFormat(0)}</Text>
                                    </View>
                                ) : (
                                    <View style={[styles.tableau, styles.border]}>
                                        <Text style={styles.labelTab}>TVA (20 %)</Text>
                                        <Text style={styles.labelTab}>{displayPriceFormat(vatAmount)}</Text>
                                    </View>
                                )}

                                <View style={styles.tableau}>
                                    <Text style={styles.totalLabel}>Montant dû TTC en livraison</Text>
                                    <Text style={styles.totalStyle}>{displayPriceFormat(totalSumFees)}</Text>
                                </View>
                            </View>
                        </View>
                    </View>

                    {renderDriverDuty()}
                </View>

                {renderFooter()}
            </Page>
        </Document>
    );
};

export default DriverInvoiceTemplate;